import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/base/Button';
import { supabase } from '../../lib/supabase';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateEmail(email)) {
      setError('Por favor ingresa un email válido');
      return;
    }

    if (!isLogin && !name.trim()) {
      setError('Por favor ingresa tu nombre');
      return;
    }
    
    if (!isLogin && password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const endpoint = isLogin ? 'sign-in' : 'sign-up';
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/auth-${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email,
            password,
            name: isLogin ? undefined : name,
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setIsLoading(false);
        return;
      }

      console.log('✅ Autenticación exitosa con Edge Function');
      console.log('🔑 Access Token:', data.session.access_token.substring(0, 20) + '...');
      console.log('🔄 Refresh Token:', data.session.refresh_token.substring(0, 20) + '...');

      // CRÍTICO: Iniciar sesión en el cliente Supabase del navegador
      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      if (sessionError) {
        console.error('❌ Error al establecer sesión en Supabase:', sessionError);
        setError('Error al iniciar sesión. Intenta nuevamente.');
        setIsLoading(false);
        return;
      }

      console.log('✅ Sesión establecida en el cliente Supabase del navegador');
      console.log('👤 User ID:', sessionData.session?.user.id);

      // Guardar sesión en localStorage (backup)
      localStorage.setItem('supabase_session', JSON.stringify({
        user: data.user,
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      }));
      
      // Guardar info del usuario
      localStorage.setItem('user', JSON.stringify({
        email: data.user.email,
        id: data.user.id,
        name: data.user.user_metadata?.name || data.user.email.split('@')[0]
      }));
      
      console.log('✅ Sesión guardada en localStorage');
      
      setIsLoading(false);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('💥 Error inesperado:', error);
      setError('Error al procesar la solicitud. Intenta nuevamente.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateEmail(email)) {
      setError('Por favor ingresa un email válido');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/auth-reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setSuccess('Te hemos enviado un email con instrucciones para recuperar tu contraseña');
        setTimeout(() => {
          setShowForgotPassword(false);
          setSuccess('');
        }, 3000);
      }
    } catch (error: any) {
      setError('Error al procesar la solicitud. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-dark to-gray-900 flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-8 group">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark rounded-xl group-hover:scale-110 transition-transform">
              <i className="ri-robot-2-line text-2xl text-white"></i>
            </div>
            <span className="text-2xl font-display font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Digital Line AI</span>
          </Link>
          
          {!showForgotPassword && (
            <div>
              <h1 className="text-3xl font-display font-bold text-white mb-2">
                {isLogin ? 'Bienvenido de nuevo' : 'Crear tu cuenta'}
              </h1>
              <p className="text-gray-300">
                {isLogin ? 'Ingresa a tu cuenta para continuar' : 'Únete y comienza a usar Mina hoy'}
              </p>
            </div>
          )}
          
          {showForgotPassword && (
            <div>
              <h1 className="text-3xl font-display font-bold text-white mb-2">
                Recuperar contraseña
              </h1>
              <p className="text-gray-300">
                Te enviaremos un enlace para restablecer tu contraseña
              </p>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl border border-gray-700/50 rounded-3xl p-8 shadow-2xl">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <i className="ri-error-warning-line text-red-400 text-xl mt-0.5"></i>
              <p className="text-red-300 text-sm flex-1">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-start gap-3">
              <i className="ri-checkbox-circle-line text-green-400 text-xl mt-0.5"></i>
              <p className="text-green-300 text-sm flex-1">{success}</p>
            </div>
          )}

          {!showForgotPassword && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                    Nombre completo
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <i className="ri-user-line text-gray-400"></i>
                    </div>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={!isLogin}
                      className="w-full pl-11 pr-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                      placeholder="Juan Pérez"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400"></i>
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-11 pr-12 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-2">
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <i className="ri-lock-line text-gray-400"></i>
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required={!isLogin}
                      className="w-full pl-11 pr-12 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </div>
              )}

              {isLogin && (
                <div className="flex justify-end">
                  <button 
                    type="button" 
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:text-primary-dark transition-colors cursor-pointer whitespace-nowrap"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              )}

              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Procesando...
                  </span>
                ) : (
                  isLogin ? 'Iniciar sesión' : 'Crear cuenta'
                )}
              </Button>
            </form>
          )}
          
          {showForgotPassword && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-white mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400"></i>
                  </div>
                  <input
                    type="email"
                    id="reset-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </span>
                ) : (
                  'Enviar enlace de recuperación'
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setSuccess('');
                }}
                className="w-full text-sm text-gray-300 hover:text-white transition-colors cursor-pointer text-center whitespace-nowrap"
              >
                Volver al inicio de sesión
              </button>
            </form>
          )}

          {!showForgotPassword && (
            <div>
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setSuccess('');
                    setConfirmPassword('');
                    setName('');
                  }}
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isLogin ? (
                    <span>
                      ¿No tienes cuenta?{' '}
                      <span className="text-primary font-semibold">Regístrate gratis</span>
                    </span>
                  ) : (
                    <span>
                      ¿Ya tienes cuenta?{' '}
                      <span className="text-primary font-semibold">Inicia sesión</span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          Al continuar, aceptas nuestros{' '}
          <Link to="/terms" className="text-primary hover:text-primary-dark transition-colors cursor-pointer whitespace-nowrap">
            Términos y condiciones
          </Link>
          {' '}y{' '}
          <Link to="/privacy" className="text-primary hover:text-primary-dark transition-colors cursor-pointer whitespace-nowrap">
            Política de privacidad
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;